import { useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';

import { EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { MissionCategoryId } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';

import { MissionCategoryAutocomplete } from './index';
import type { MissionCategoryValue } from './types/mission-category-value.type';

interface Props {
  readonly required?: boolean;
}

interface FormValues {
  readonly categoryParentId: string | null;
  readonly categoryLeaf: string | null;
}

const LEAF_FIELD = 'categoryLeaf';

export const MissionCategoryFormField = ({
  required = true,
}: Readonly<Props>) => {
  const { errors, setFieldTouched, setValues, touched, values } =
    useFormikContext<FormValues>();

  const handleChange = useCallback(
    ({ parentId, leaf }: MissionCategoryValue) =>
      void setValues((formValues) => ({
        ...formValues,
        categoryParentId: parentId ?? EMPTY_ID,
        categoryLeaf: leaf,
      })),
    [setValues],
  );

  const handleBlur = useCallback(
    () => void setFieldTouched(LEAF_FIELD, true),
    [setFieldTouched],
  );

  const parentId =
    values.categoryParentId && values.categoryParentId !== EMPTY_ID
      ? (values.categoryParentId as MissionCategoryId)
      : null;

  return (
    <Grid size={12} sx={{ pt: 1, pb: 1 }}>
      <MissionCategoryAutocomplete
        error={!!(getIn(errors, LEAF_FIELD) && getIn(touched, LEAF_FIELD))}
        helperText={getTouchedError(errors, touched, LEAF_FIELD)}
        name="categoryParentId"
        onBlur={handleBlur}
        onChange={handleChange}
        required={required}
        value={{ parentId, leaf: values.categoryLeaf ?? EMPTY_STRING }}
      />
    </Grid>
  );
};
